import { artScale, type Size } from "odyssey-shared";
import type { ArtBox } from "../../ui/kit/ArtIcon.tsx";
import type { OverlayProperties } from "../overlay.ts";

/**
 * The pop-out's pure rules (ADR-100's amendment of 2026-10-09, decisions 30 and 32, and of 2026-10-14, decision 50): the
 * window's size in device pixels, the UI scale and layout that size alone gives, and the size kept on the device between
 * openings.
 */

/**
 * The size the pop-out opens at when none is kept, in the window's own (CSS) pixels.
 */
export const POPOUT_DEFAULT: Size = Object.freeze({ width: 320, height: 320 });

/**
 * Where the size last kept lives on the device, as `WIDTHxHEIGHT`.
 */
export const POPOUT_SIZE_KEY = "odyssey.map.popout.size";

/**
 * The smallest side the pop-out is laid out for, in logical pixels, and the smallest side it keeps.
 */
export const POPOUT_LEAST = 240;

/**
 * The widest or tallest size kept, so a stray number never asks a browser for a window it cannot make.
 */
export const POPOUT_MOST = 4000;

const WIDE_LEAST = 360;

/**
 * How the pop-out lays itself out: `beside` and `below` are the roomy layouts (the window's own controls over the map),
 * `small` has a header of 16 pixel icon buttons.
 */
export type PopoutArrangement = "beside" | "below" | "small";

/**
 * What the pop-out's device size gives.
 */
export interface PopoutLayout {
  readonly scale: number;
  readonly logical: Size;
  readonly layout: PopoutArrangement;
}

/**
 * A window's size in device pixels: its inner size times the device pixel ratio, rounded, as the game's viewport is
 * measured (ADR-061 draws in whole device pixels).
 *
 * @param inner The window's inner width and height in CSS pixels.
 * @param ratio The device pixel ratio; anything that is not a positive number is taken as 1.
 * @returns The size in whole device pixels.
 */
export function deviceSize(inner: Size, ratio: number): Size {
  const by = Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
  return { width: Math.round(inner.width * by), height: Math.round(inner.height * by) };
}

/**
 * The pop-out's UI scale and layout from its size in device pixels. The scale is the largest whole number at which the
 * size still holds 240 by 240 logical pixels, at least 1; the logical size is the device size over the scale. At least
 * 360 wide and at least as wide as tall is `beside`; at least 360 tall and taller than wide is `below`; anything else is
 * `small`.
 *
 * The viewer's Interface size follows as far as the window holds it: a smaller step takes the scale down from the one
 * above, to 1 at least, and a larger step changes nothing, since that scale is already the largest the size holds.
 *
 * @param device The window's size in device pixels.
 * @param step The viewer's Interface size step, 0 for Auto.
 * @returns The scale, the logical size and the layout.
 */
export function popoutLayout(device: Size, step = 0): PopoutLayout {
  const fit = Math.max(1, Math.floor(Math.min(device.width, device.height) / POPOUT_LEAST));
  const scale = Math.min(fit, Math.max(1, fit + Math.round(step)));
  const logical: Size = { width: Math.floor(device.width / scale), height: Math.floor(device.height / scale) };
  const layout: PopoutArrangement = logical.width >= WIDE_LEAST && logical.width >= logical.height ? "beside" : logical.height >= WIDE_LEAST && logical.height > logical.width ? "below" : "small";
  return { scale, logical, layout };
}

/**
 * Reads the size kept for the pop-out.
 *
 * @param raw The kept text, or null when nothing is kept.
 * @returns The size in CSS pixels; the default for anything that is not `WIDTHxHEIGHT` of two whole numbers from 240 to 4000.
 */
export function parsePopoutSize(raw: string | null): Size {
  const found = raw === null ? null : /^(\d{1,5})x(\d{1,5})$/.exec(raw);
  if (found === null) {
    return POPOUT_DEFAULT;
  }
  const width = Number(found[1]);
  const height = Number(found[2]);
  if (width < POPOUT_LEAST || height < POPOUT_LEAST || width > POPOUT_MOST || height > POPOUT_MOST) {
    return POPOUT_DEFAULT;
  }
  return { width, height };
}

/**
 * The text a size is kept as, each side rounded and held to what `parsePopoutSize` reads back.
 *
 * @param size The window's inner size in CSS pixels.
 * @returns `WIDTHxHEIGHT`.
 */
export function formatPopoutSize(size: Size): string {
  const hold = (side: number): number => Math.min(POPOUT_MOST, Math.max(POPOUT_LEAST, Math.round(side)));
  return `${hold(size.width)}x${hold(size.height)}`;
}

/**
 * One art pixel of a box as a CSS length, as the overlay root writes it.
 *
 * @param box The box.
 * @param scale The UI scale.
 * @param ratio The device pixel ratio.
 * @returns Device pixels in CSS pixels.
 */
function artProperty(box: ArtBox, scale: number, ratio: number): string {
  return `${artScale(box, scale) / ratio}px`;
}

/**
 * The custom properties the pop-out's root carries, the overlay root's own for the pop-out's scale: `--ui-scale`, `--px`
 * (the scale in CSS pixels, so one logical pixel is a whole number of device pixels) and one art pixel of each box.
 *
 * @param device The window's size in device pixels.
 * @param ratio The window's device pixel ratio; anything that is not a positive number is taken as 1.
 * @param step The viewer's Interface size step, 0 for Auto.
 * @returns The properties.
 */
export function popoutProperties(device: Size, ratio: number, step = 0): OverlayProperties {
  const by = Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
  const { scale } = popoutLayout(device, step);
  return { "--ui-scale": String(scale), "--px": `${scale / by}px`, "--art-48": artProperty(48, scale, by), "--art-32": artProperty(32, scale, by), "--art-22": artProperty(22, scale, by), "--art-16": artProperty(16, scale, by), "--art-13": artProperty(13, scale, by), "--art-10": artProperty(10, scale, by), "--art-8": artProperty(8, scale, by) };
}
